import React, { useState, useEffect } from 'react';
import { Button, Drawer, Descriptions, Input, Space, Alert, Tag, Select } from 'antd';
import AntTable from '../../components/CustomTable/AntTable';
import { getHeldTransactions, getHeldTransactionDetail, proposeMakerApprove, proposeMakerReject, executeCheckerApprove, executeCheckerReject } from '../../services/fraudCheckService';
import { NotificationManager } from 'react-notifications';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';

const { Search } = Input;
const { Option } = Select;

const HeldTransactionsTab = () => {
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState(null);

  // Filters state
  const [statusFilter, setStatusFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');

  // Drawer for Detail View
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // User roles check
  const [roles, setRoles] = useState([]);
  const [isMaker, setIsMaker] = useState(false);
  const [isChecker, setIsChecker] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Extract roles on load
  useEffect(() => {
    const rawRoles = localStorage.getItem('roles');
    let userRoles = [];
    try {
      userRoles = JSON.parse(rawRoles || '[]');
      if (!Array.isArray(userRoles)) {
        userRoles = [userRoles];
      }
    } catch {
      userRoles = rawRoles ? [rawRoles] : [];
    }
    setRoles(userRoles);
    setIsMaker(userRoles.includes('SUPER_ADMIN') || userRoles.includes('OWNER'));
    setIsChecker(userRoles.includes('OWNER'));
  }, []);

  const fetchHeldTx = async (page = 1, size = 10) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: page - 1,
        size,
      };
      if (statusFilter) {
        params.status = statusFilter;
      }
      if (userIdFilter) {
        params.userId = userIdFilter;
      }

      const res = await getHeldTransactions(params);

      // Spring Page response structure
      const content = res?.data?.content || res?.content || res?.data || (Array.isArray(res) ? res : []);
      const totalElements = res?.data?.totalElements || res?.totalElements || res?.total || content.length;

      // Inject standard 'id' for AntTable's rowKey
      const formatted = content.map((item, index) => ({
        ...item,
        id: item.id || item.reference || `held_${index}`,
      }));

      setTransactions(formatted);
      setPagination({
        current: page,
        pageSize: size,
        total: totalElements,
      });
    } catch (err) {
      setError(err.message || 'Failed to fetch held transactions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHeldTx(1, 10);
  }, [statusFilter, userIdFilter]);

  const handleTableChange = ({ pagination: newPagination }) => {
    fetchHeldTx(newPagination.current, newPagination.pageSize);
  };

  const handleSearchUserId = (val) => {
    setUserIdFilter(val.trim());
  };

  const handleInspect = async (record) => {
    setDetailLoading(true);
    setDrawerVisible(true);
    try {
      if (record.reference) {
        const detailRes = await getHeldTransactionDetail(record.reference);
        if (detailRes && (detailRes.data || detailRes.success)) {
          setSelectedTx(detailRes.data || detailRes);
        } else {
          setSelectedTx(record);
        }
      } else {
        setSelectedTx(record);
      }
    } catch (err) {
      console.error('Error fetching transaction details, falling back to table record:', err);
      setSelectedTx(record);
    } finally {
      setDetailLoading(false);
    }
  };

  // Maker Actions
  const handleMakerPropose = (record, actionType) => {
    const isApprove = actionType === 'APPROVE';
    Swal.fire({
      title: `${isApprove ? 'Approval' : 'Rejection'}`,
      text: `${isApprove ? 'APPROVE' : 'REJECT'} for transaction ${record.reference}?`,
      icon: 'question',
      input: 'textarea',
      inputPlaceholder: 'Enter proposal comment here (required)...',
      showCancelButton: true,
      confirmButtonColor: isApprove ? '#28a745' : '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: `Submit Proposal`,
      preConfirm: (inputValue) => {
        if (!inputValue || !inputValue.trim()) {
          Swal.showValidationMessage('Proposal comment is required');
        }
        return inputValue;
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const useCheckerEndpoint = roles.includes('OWNER');
          const apiCall = useCheckerEndpoint
            ? (isApprove ? executeCheckerApprove : executeCheckerReject)
            : (isApprove ? proposeMakerApprove : proposeMakerReject);
          const res = await apiCall(record.reference, result.value);
          if (res && (res.success || res.status === true)) {
            NotificationManager.success(res.message || 'Proposal submitted successfully');
            setDrawerVisible(false);
            fetchHeldTx(pagination.current, pagination.pageSize);
          } else {
            NotificationManager.error(res?.message || 'Failed to submit proposal');
          }
        } catch (err) {
          NotificationManager.error(err.message || 'An error occurred while proposing');
        }
      }
    });
  };

  // Checker Actions
  const handleCheckerFinalize = (record, actionType) => {
    const isApprove = actionType === 'APPROVE';
    Swal.fire({
      title: `Execute Final ${isApprove ? 'Approval' : 'Rejection'}`,
      text: `Confirm final ${isApprove ? 'APPROVE' : 'REJECT'} for transaction ${record.reference}?`,
      icon: isApprove ? 'success' : 'warning',
      input: 'textarea',
      inputPlaceholder: 'Enter final approval/rejection comment (required)...',
      showCancelButton: true,
      confirmButtonColor: isApprove ? '#28a745' : '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: isApprove ? 'Execute Approve' : 'Veto Reject',
      preConfirm: (inputValue) => {
        if (!inputValue || !inputValue.trim()) {
          Swal.showValidationMessage('Decision comment is required');
        }
        return inputValue;
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const apiCall = isApprove ? executeCheckerApprove : executeCheckerReject;
          const res = await apiCall(record.reference, result.value);
          if (res && (res.success || res.status === true)) {
            NotificationManager.success(res.message || 'Final decision executed successfully');
            setDrawerVisible(false);
            fetchHeldTx(pagination.current, pagination.pageSize);
          } else {
            NotificationManager.error(res?.message || 'Failed to execute decision');
          }
        } catch (err) {
          NotificationManager.error(err.message || 'An error occurred while processing decision');
        }
      }
    });
  };

  const getStatusTagColor = (status) => {
    const val = String(status || '').toUpperCase();
    if (val === 'APPROVED') return 'success';
    if (val === 'REJECTED') return 'error';
    if (val === 'PENDING_CHECKER') return 'processing';
    if (val === 'PENDING_MAKER') return 'warning';
    return 'default';
  };

  const columns = [
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (val, record) => {
        const currency = record.currency || 'NGN';
        return val ? `${currency} ${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : 'N/A';
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusTagColor(status)} style={{ fontWeight: 600 }}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Maker Reason',
      dataIndex: 'makerComment',
      key: 'makerComment',
      ellipsis: true,
      render: (text) => text || 'N/A',
    },
    {
      title: 'Date Held',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : 'N/A'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const status = String(record.status || '').toUpperCase();
        const canMakerAction = isMaker && status === 'PENDING_MAKER';
        const canCheckerAction = isChecker && status === 'PENDING_CHECKER';

        return (
          <Space size="small">
            <Button type="default" size="small" onClick={() => handleInspect(record)}>
              Inspect
            </Button>


            <>
              <Button type="primary" size="small" style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }} onClick={() => handleMakerPropose(record, 'APPROVE')}>
                Approve
              </Button>
              <Button type="primary" danger size="small" onClick={() => handleMakerPropose(record, 'REJECT')}>
                Reject
              </Button>
            </>

            {/* {canCheckerAction && ( */}
            <>
              <Button type="primary" size="small" style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }} onClick={() => handleCheckerFinalize(record, 'APPROVE')}>
                Approve
              </Button>
              <Button type="primary" danger size="small" onClick={() => handleCheckerFinalize(record, 'REJECT')}>
                Reject
              </Button>
            </>
            {/* )} */}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

      <div className="fraud-toolbar">
        <div className="fraud-filters">
          <Select
            placeholder="Filter by Status"
            allowClear
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 180 }}
          >
            <Option value="PENDING_MAKER">PENDING MAKER</Option>
            <Option value="PENDING_CHECKER">PENDING CHECKER</Option>
            <Option value="APPROVED">APPROVED</Option>
            <Option value="REJECTED">REJECTED</Option>
          </Select>

          <Search
            placeholder="Search by User ID..."
            allowClear
            onSearch={handleSearchUserId}
            onChange={(e) => handleSearchUserId(e.target.value)}
            style={{ width: 250 }}
          />
        </div>

        <Button type="default" onClick={() => fetchHeldTx(pagination.current, pagination.pageSize)} loading={loading}>
          Refresh
        </Button>
      </div>

      <AntTable
        loading={loading}
        users={transactions}
        pagination={pagination}
        handleGetNext={handleTableChange}
        columns={columns}
      />

      <Drawer
        title={`Held Transaction Details: ${selectedTx?.reference || ''}`}
        placement="right"
        width={650}
        onClose={() => setDrawerVisible(false)}
        visible={drawerVisible}
        className="fraud-detail-drawer"
        extra={
          selectedTx && (
            <Space>
              {isMaker && String(selectedTx.status).toUpperCase() === 'PENDING_MAKER' && (
                <>
                  <Button type="primary" style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }} onClick={() => handleMakerPropose(selectedTx, 'APPROVE')}>
                    Approve
                  </Button>
                  <Button type="primary" danger onClick={() => handleMakerPropose(selectedTx, 'REJECT')}>
                    Reject
                  </Button>
                </>
              )}
              {isChecker && String(selectedTx.status).toUpperCase() === 'PENDING_CHECKER' && (
                <>
                  <Button type="primary" style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }} onClick={() => handleCheckerFinalize(selectedTx, 'APPROVE')}>
                    Approve
                  </Button>
                  <Button type="primary" danger onClick={() => handleCheckerFinalize(selectedTx, 'REJECT')}>
                    Reject
                  </Button>
                </>
              )}
            </Space>
          )
        }
      >
        {detailLoading ? (
          <div>Loading details...</div>
        ) : selectedTx ? (
          (
            <div className="fraud-detail-container">
              <div className="fraud-detail-section">
                <h4>Transaction Information</h4>
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="Reference">{selectedTx.reference}</Descriptions.Item>
                  <Descriptions.Item label="Transaction Type">{selectedTx.transactionType || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="User ID">{selectedTx.userId || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Amount">
                    {selectedTx.amount != null ? `${selectedTx.currency || 'NGN'} ${Number(selectedTx.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="From Account">{selectedTx.fromAccountNumber || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="To Account">{selectedTx.toAccountNumber || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="To Bank Code">{selectedTx.toBankCode || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={getStatusTagColor(selectedTx.status)} style={{ fontWeight: 600 }}>
                      {selectedTx.status}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Trigger Reason">{selectedTx.triggerReason || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Date Held">
                    {selectedTx.createdAt ? dayjs(selectedTx.createdAt).format('YYYY-MM-DD HH:mm:ss') : 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Expires At">
                    {selectedTx.expiresAt ? dayjs(selectedTx.expiresAt).format('YYYY-MM-DD HH:mm:ss') : 'N/A'}
                  </Descriptions.Item>
                </Descriptions>
              </div>

              <div className="fraud-detail-section">
                <h4>Customer Context</h4>
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="Customer IP">{selectedTx.customerIp || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="User Agent">{selectedTx.customerUserAgent || 'N/A'}</Descriptions.Item>
                </Descriptions>
              </div>

              <div className="fraud-detail-section">
                <h4>Maker Audit</h4>
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="Maker ID">{selectedTx.makerId || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Maker Name">{selectedTx.makerName || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Maker Action">{selectedTx.makerAction || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Maker Comment">{selectedTx.makerComment || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Maker IP">{selectedTx.makerIp || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Maker Decided At">
                    {selectedTx.makerDecidedAt ? dayjs(selectedTx.makerDecidedAt).format('YYYY-MM-DD HH:mm:ss') : 'N/A'}
                  </Descriptions.Item>
                </Descriptions>
              </div>

              <div className="fraud-detail-section">
                <h4>Checker Audit</h4>
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="Checker ID">{selectedTx.checkerId || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Checker Name">{selectedTx.checkerName || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Checker Action">{selectedTx.checkerAction || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Checker Comment">{selectedTx.checkerComment || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Checker IP">{selectedTx.checkerIp || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Checker Decided At">
                    {selectedTx.checkerDecidedAt ? dayjs(selectedTx.checkerDecidedAt).format('YYYY-MM-DD HH:mm:ss') : 'N/A'}
                  </Descriptions.Item>
                </Descriptions>
              </div>

              {(selectedTx.executedTransactionId || selectedTx.executionError) && (
                <div className="fraud-detail-section">
                  <h4>Execution Result</h4>
                  <Descriptions bordered column={1} size="small">
                    <Descriptions.Item label="Executed Transaction ID">{selectedTx.executedTransactionId || 'N/A'}</Descriptions.Item>
                    <Descriptions.Item label="Execution Error">{selectedTx.executionError || 'N/A'}</Descriptions.Item>
                  </Descriptions>
                </div>
              )}

            </div>
          )
        ) : (
          <div>No record selected</div>
        )}
      </Drawer>
    </div>
  );
};

export default HeldTransactionsTab;
